import React, { useState, useRef } from "react";
import Webcam from "react-webcam";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import { useTheme } from "@mui/material/styles";
import makeStyles from "@mui/styles/makeStyles";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import useMediaQuery from "@mui/material/useMediaQuery";
import RotateLeftIcon from "@mui/icons-material/RotateLeft";
import PublishIcon from "@mui/icons-material/Publish";
import { SaveLoader } from "@/_components";
import { toast } from "react-toastify";
import { jsPDF } from "jspdf";

const useStyles = makeStyles((theme) => ({
  labelToBtn: {
    backgroundColor: "#1c355e",
    color: "#fff",
    cursor: "pointer",
    outline: 0,
    border: 0,
    margin: 0,
    justifyContent: "center",
    verticalAlign: "middle",
    font: "400 13.3333px Arial",
    lineHeight: 1.75,
    letterSpacing: "0.02857em",
    textTransform: "uppercase",
    padding: "10px 16px",
    borderRadius: 4,
    "&:hover": {
      backgroundColor: "#132541",
      boxShadow:
        "0px 2px 4px -1px rgb(0 0 0 / 20%), 0px 4px 5px 0px rgb(0 0 0 / 14%), 0px 1px 10px 0px rgb(0 0 0 / 12%)",
    },
  },
  root: {
    flexGrow: 1,
    [theme.breakpoints.up("sm")]: { width: "80vw", maxWidth: 800 },
    [theme.breakpoints.down("sm")]: { maxWidth: 300 },
  },
  pdfContainer: {
    [theme.breakpoints.up("sm")]: { width: "80vw", maxWidth: 800 },
    [theme.breakpoints.down("sm")]: { width: "60vw", maxWidth: 300 },
  },
  customToolbar: {
    backgroundColor: theme.palette.background.paper,
  },
  actionButton: {
    backgroundColor: "#76BC21",
    "&:hover": {
      backgroundColor: "#edae49",
    },
    color: "white",
    marginTop: 5,
    fontWeight: 600,
  },
  tabButton: {
    [theme.breakpoints.up("sm")]: {
      paddingBottom: "20px",
      paddingLeft: "20px",
    },
    [theme.breakpoints.down("sm")]: {
      padding: "10px",
      textAlign: "center",
    },
  },
}));

const PhotoTake = ({ submitting, setImageData, imageData, uploadClick }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const webcamRef = useRef(null);
  const [imgSrc, setImgSrc] = useState(null);
  const [showWebcam, setShowWebcam] = useState(null);
  // const [isFileFromWebcam, setIsFileFromWebcam] = useState(false);

  const videoConstraints = {
    width: 1280,
    height: 720,
    facingMode: "user",
  };

  const WebcamCapture = () => {
    const theme = useTheme();
    const isXtraSmall = useMediaQuery(theme.breakpoints.down("sm"));

    // const capture = React.useCallback(() => {
    //   console.log(navigator.mediaDevices);
    //   const imageSrc = webcamRef.current.getScreenshot();
    //   if(imageSrc){
    //     setImgSrc(imageSrc);
    //     let iWidth = webcamRef.current.canvas.width;
    //     let iHeight = webcamRef.current.canvas.height;

    //     let doc = new jsPDF("p", "px", "letter");
    //     let maxWidth = doc.internal.pageSize.getWidth();
    //     let maxHeight = doc.internal.pageSize.getHeight();
    //     let ratio = Math.min(maxWidth / iWidth, maxHeight / iHeight);
    //     iWidth = iWidth * ratio;
    //     iHeight = iHeight * ratio;
    //     doc.addImage(imageSrc, "JPEG", 0, 0, iWidth, iHeight);
    //     //doc.save(webCamCaptureFileName);
    //     var dataSrc = doc.output("datauristring"); // base64 string
    //     // setIsFileFromWebcam(true);
    //     setImageData({
    //       imageFile: dataSrc,
    //       imageSrc: imageSrc,
    //     });
    //  }
    // }, [webcamRef, imgSrc]);

    const capture = React.useCallback(() => {
      if (webcamRef.current) {
        const imageSrc = webcamRef.current.getScreenshot();
        if (imageSrc) {
          setImgSrc(imageSrc);

          const base64 = imageSrc.split(",")[1];
          const byteCharacters = atob(base64);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], { type: "image/jpeg" });
          const imageFile = new File([blob], "webcam_capture.jpg", {
            type: "image/jpeg",
          });

          const img = new Image();
          img.src = imageSrc;
          img.onload = () => {
            const iWidth = img.width;
            const iHeight = img.height;

            const doc = new jsPDF("p", "px", "letter");
            const maxWidth = doc.internal.pageSize.getWidth();
            const maxHeight = doc.internal.pageSize.getHeight();

            const ratio = Math.min(maxWidth / iWidth, maxHeight / iHeight);
            const scaledWidth = iWidth * ratio;
            const scaledHeight = iHeight * ratio;

            doc.addImage(imageSrc, "JPEG", 0, 0, scaledWidth, scaledHeight);

            const pdfDataUri = doc.output("datauristring");
            const pdfBase64 = pdfDataUri.split(",")[1];

            setImageData({
              imageFile: imageFile, // Ankit- the actual File object
              imageSrc: imageSrc, // Ankit - data URI of the image
              pdfBase64: pdfBase64, // Ankit -  base64 of the generated PDF
            });
          };
        }
      }
    }, [webcamRef, imgSrc]);

    const handleClick = () => {
      navigator.mediaDevices
        .getUserMedia({ video: true })
        .then(() => {
          setShowWebcam(true);
        })
        .catch((err) => {
          toast.error(t("documents.permissionDenied") + " " + err.message);
        });
    };

    return (
      <>
        {showWebcam && (
          <Webcam
            audio={false}
            height={isXtraSmall ? 200 : 240}
            ref={webcamRef}
            screenshotFormat="image/jpeg"
            width={isXtraSmall ? 300 : 320}
            videoConstraints={videoConstraints}
          />
        )}
        <Grid
          container
          item
          style={{ paddingTop: 10, paddingBottom: 10 }}
          justifyContent="center"
        >
          {showWebcam ? (
            <Button
              size="medium"
              variant="contained"
              sx={{ backgroundColor: "#1c355e" }}
              color="primary"
              onClick={capture}
            >
              {t("documents.photoUpload")}
            </Button>
          ) : submitting === false ? (
            <Button
              size="medium"
              variant="contained"
              sx={{ backgroundColor: "#1c355e" }}
              color="primary"
              onClick={handleClick}
            >
              {t("documents.photoUpload")}
            </Button>
          ) : (
            <SaveLoader />
          )}
        </Grid>
      </>
    );
  };

  return (
    <>
      <div className={classes.root}>
        <Grid
          container
          direction="row"
          justifyContent="center"
          alignItems="center"
        >
          <Grid item xs={12}>
            {imageData ? (
              <Grid item>
                <img
                  src={imageData.imageSrc}
                  alt="Picture"
                  style={{ height: "100%", width: "100%" }}
                />
              </Grid>
            ) : (
              <></>
            )}
            {imageData ? (
              <Grid item>
                <Grid
                  container
                  direction="row"
                  justifyContent="center"
                  style={{ paddingTop: 10 }}
                >
                  <Grid item className={classes.tabButton}>
                    <Button
                      disabled={submitting}
                      size="medium"
                      variant="contained"
                      color="primary"
                      onClick={() => setImageData(null)}
                      startIcon={
                        <RotateLeftIcon style={{ fontSize: "1.4em" }} />
                      }
                    >
                      {t("documents.retake")}
                    </Button>
                  </Grid>
                  <Grid item className={classes.tabButton}>
                    {submitting === false ? (
                      <Button
                        // disabled={submitting}
                        size="medium"
                        variant="contained"
                        color="primary"
                        onClick={() => uploadClick("Photo")}
                        startIcon={
                          <PublishIcon style={{ fontSize: "1.4em" }} />
                        }
                      >
                        {t("documents.upload")}
                      </Button>
                    ) : (
                      <SaveLoader />
                    )}
                  </Grid>
                </Grid>
              </Grid>
            ) : (
              <>
                <Grid container item justifyContent="center">
                  <WebcamCapture />
                </Grid>
              </>
            )}
          </Grid>
        </Grid>
      </div>
    </>
  );
};

PhotoTake.propTypes = {
  selectedUploadTypeID: PropTypes.number,
  selectedUploadName: PropTypes.string,
  uploadClick: PropTypes.func,
  imageData: PropTypes.object,
  setImageData: PropTypes.func,
  submitting: PropTypes.bool,
};

export default PhotoTake;
